import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MetricName, Timeseries } from "../types";
import { metricLabels } from "../types";
import { cellValue } from "../format";
import { dateLabel } from "../dateRange";
import { Empty } from "./States";
import styles from "../Analytics.module.css";
export default function TrendChart({
  data,
  compare,
}: {
  data: Timeseries;
  compare: boolean;
}) {
  const label = metricLabels[data.metric as MetricName] ?? data.metric;
  const points = data.points ?? [];
  return (
    <section className={styles.panel} aria-label={`${label} trend`}>
      <div className={styles.panelHeading}>
        <div>
          <h2>{label} over time</h2>
          <p>
            {data.source === "gsc" ? "Search Console" : "Google Analytics"} ·
            Daily results
          </p>
        </div>
        <div className={styles.legend}>
          <span>
            <i />
            Selected period
          </span>
          {compare && (
            <span>
              <i className={styles.previous} />
              Previous period
            </span>
          )}
        </div>
      </div>
      {!points.some((p) => p.value !== null) ? (
        <Empty title="No trend available">
          There are no reliable daily results for this selection. Refresh data
          or choose another period.
        </Empty>
      ) : (
        <>
          <div className={styles.chart}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={points}
                margin={{ top: 12, right: 16, left: 0, bottom: 5 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="var(--color-border)"
                  strokeDasharray="3 5"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => dateLabel(String(d)).split(",")[0]}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={45}
                  stroke="var(--color-text-secondary)"
                  fontSize={11}
                />
                <YAxis
                  tickFormatter={(value) => cellValue(value, data.metric)}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                  stroke="var(--color-text-secondary)"
                  fontSize={11}
                />
                <Tooltip
                  labelFormatter={(d) => dateLabel(String(d))}
                  formatter={(v: number, name: string) => [
                    cellValue(v, data.metric),
                    name === "value" ? "Selected period" : "Previous period",
                  ]}
                  contentStyle={{
                    background: "var(--color-background-primary)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    color: "var(--color-text-primary)",
                  }}
                />
                {compare && (
                  <Line
                    type="monotone"
                    dataKey="previous"
                    stroke="var(--color-text-tertiary)"
                    strokeDasharray="4 5"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="var(--color-accent-700)"
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className={styles.caption}>
            Gaps mean unavailable data. The comparison is aligned by day in each
            period.
          </p>
          <details className={styles.chartData}>
            <summary>View chart as a table</summary>
            <div className={styles.tableScroll}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">{label}</th>
                    {compare && <th scope="col">Previous period</th>}
                  </tr>
                </thead>
                <tbody>
                  {points.map((p) => (
                    <tr key={p.date}>
                      <th scope="row">{dateLabel(p.date)}</th>
                      <td>{cellValue(p.value, data.metric)}</td>
                      {compare && <td>{cellValue(p.previous, data.metric)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
}
